<template>
  <div
    class="flex h-full min-h-[96px] flex-col gap-1.5 overflow-hidden p-2"
    :class="{ 'schedule-cell--drop-target': dropEligible && dropHover }"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <template v-if="cell?.hasPlan">
      <!-- The cell is a fixed height (the board sizes it), so a week with more blocks than
           fit scrolls here rather than pushing the whole row taller. The padding is what the
           cards' 2px selection/hover ring sits in: overflow-y on one axis makes the other
           axis scroll too, and the ring would be clipped against a flush edge. -->
      <div ref="cardList" class="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-0.5">
        <!-- A div, not a button: the actions menu below is a real button, and a button
             cannot nest inside one. -->
        <div
          v-for="(group, index) in cell.groups"
          :key="group.key"
          :data-block-key="groupBlockKey(group)"
          :data-group-key="group.key"
          class="schedule-card w-full shrink-0 rounded border-l-[3px] px-2 py-1 text-left"
          :class="[
            groupBlockKey(group) === selectedPoNumber ? 'bg-blue-50' : 'bg-gray-50',
            group.key === selectedBlockKey ? 'schedule-card--selected' : '',
            groupBlockKey(group) === hoveredBlockKey ? 'schedule-card--hovered' : '',
          ]"
          :style="group.type === ASSIGNMENT_TYPE.manual ? manualStyle : { borderLeftColor: groupColor(group, index) }"
          :draggable="canWrite"
          role="button"
          tabindex="0"
          @click="emit('selectGroup', group)"
          @keydown.enter.prevent="emit('selectGroup', group)"
          @keydown.space.prevent="emit('selectGroup', group)"
          @contextmenu.prevent="openCardMenu"
          @dragstart="onDragStart(group, $event)"
          @dragend="emit('dragEnd')"
          @mouseenter="onCardHoverStart(group, $event)"
          @mouseleave="onCardHoverEnd()"
        >
          <span class="flex items-center gap-1 text-[11px] font-semibold text-gray-900">
            <!-- A reservation's name is its comment, so it can run long; the native title
                 carries the whole of it. -->
            <span class="grow truncate" :title="groupLabel(group)">{{ groupLabel(group) }}</span>
            <!-- Without this the note is invisible until someone opens the actions menu. Hover
                 reads it; pressing opens the same dialog the menu's note item does. -->
            <v-tooltip v-if="groupNoteNeedsMarker(group)" location="bottom" open-delay="150" max-width="320">
              <template #activator="{ props: noteProps }">
                <button
                  v-bind="noteProps"
                  type="button"
                  class="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-gray-500 hover:text-gray-900"
                  :aria-label="`View note for ${groupLabel(group)}`"
                  @click.stop="emit('viewNote', group)"
                  @mousedown.stop
                  @keydown.enter.stop.prevent="emit('viewNote', group)"
                  @keydown.space.stop.prevent="emit('viewNote', group)"
                >
                  <v-icon icon="mdi-note-text-outline" size="12" />
                </button>
              </template>
              <span class="whitespace-pre-line text-xs">{{ group.notes }}</span>
            </v-tooltip>
            <span class="shrink-0">{{ group.totalQuantity.toLocaleString() }}</span>
            <v-menu location="bottom end">
              <template #activator="{ props: menuProps }">
                <!-- mousedown is stopped so pressing the menu does not start dragging the
                     card underneath it; click, so it does not also select the block. -->
                <button
                  v-bind="menuProps"
                  type="button"
                  class="schedule-card__menu-button -mr-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-slate-200 hover:text-gray-900"
                  :aria-label="`Actions for ${groupLabel(group)}`"
                  @click.stop
                  @mousedown.stop
                  @keydown.enter.stop
                  @keydown.space.stop
                >
                  ⋮
                </button>
              </template>
              <div class="schedule-card-menu">
                <button
                  v-if="canWrite || group.notes"
                  type="button"
                  class="schedule-card-menu__item"
                  @click="emit('viewNote', group)"
                >
                  {{ noteMenuLabel(group) }}
                </button>
                <button v-if="canWrite" type="button" class="schedule-card-menu__item" @click="emit('editCapacity', group)">Edit capacity</button>
                <router-link class="schedule-card-menu__item" :to="calendarLink(group)">Capacity Calendar</router-link>
                <router-link v-if="canViewActivity" class="schedule-card-menu__item" :to="activityLink(group)">Activity log</router-link>
                <button v-if="canViewLineItems(group)" type="button" class="schedule-card-menu__item" @click="emit('viewLineItems', group)">
                  View line items
                </button>
                <button
                  v-if="canWrite"
                  type="button"
                  class="schedule-card-menu__item schedule-card-menu__item--danger"
                  @click="emit('remove', group)"
                >
                  Remove
                </button>
              </div>
            </v-menu>
          </span>
          <v-tooltip location="bottom" open-delay="150" :disabled="!subLineOverflowing[group.key]">
            <template #activator="{ props: activatorProps }">
              <span v-bind="activatorProps" class="schedule-card__subline block truncate text-[10px] text-gray-500">
                {{ subLine(group) }}
              </span>
            </template>
            <span class="text-xs">{{ subLine(group) }}</span>
          </v-tooltip>
        </div>
      </div>

      <div class="pt-1">
        <div class="h-1.5 overflow-hidden rounded-full" :class="isOverbooked ? 'bg-red-200' : 'bg-gray-200'">
          <div class="h-full rounded-full" :class="isOverbooked ? 'bg-red-500' : 'bg-blue-600'" :style="{ width: meterWidth }" />
        </div>
        <p class="mb-0 mt-0.5 text-[10px]" :class="isOverbooked ? 'font-semibold text-red-700' : 'text-gray-500'">
          <template v-if="isOverbooked">{{ overbookedLabel }}</template>
          <template v-else>{{ cell.scheduledTotal.toLocaleString() }} scheduled · {{ cell.remainingTotal.toLocaleString() }} left</template>
        </p>
      </div>
    </template>
    <p v-else class="m-0 self-center text-[11px] italic text-gray-300">No plan</p>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
  import type { RouteLocationRaw } from 'vue-router';

  import { useAssignmentHover } from '@/composables/useAssignmentHover';
  import { useCalendarPrivacyStore } from '@/store/calendarPrivacyStore';
  import { useCustomerMappingStore } from '@/store/customerMappingStore';
  import { ASSIGNMENT_TYPE } from '@/types/capacityCalendar';
  import { resolvePoSegmentColor, UNMAPPED_PO_COLOR } from '@/utils/customerColors';
  import { activityLinkQuery, calendarLinkQuery, groupBlockKey, groupHasLineItems, groupLabel, groupNoteNeedsMarker } from '@/utils/scheduleBoard';
  import type { ScheduleGroup, VendorWeekCell } from '@/utils/scheduleBoard';

  const FULL_PERCENT = 100;

  const props = defineProps<{
    cell?: VendorWeekCell;
    canWrite: boolean;
    // Superadmin-only page; partners never see the item rather than getting a 403 page.
    canViewActivity?: boolean;
    dropEligible?: boolean;
    selectedPoNumber?: string | null;
    // The ring marks the one card that was clicked; every other card of the same PO keeps
    // only the tint, so a distribution reads as a family with one member singled out.
    selectedBlockKey?: string | null;
    // The week this cell stands for, and the vendor behind it — the actions menu builds its
    // own deep link and decides whether a block has line items to read.
    weekKey: string;
    vendorId?: string;
  }>();
  const emit = defineEmits<{
    selectGroup: [group: ScheduleGroup];
    viewNote: [group: ScheduleGroup];
    editCapacity: [group: ScheduleGroup];
    viewLineItems: [group: ScheduleGroup];
    remove: [group: ScheduleGroup];
    dragStart: [group: ScheduleGroup];
    dragEnd: [];
    drop: [];
  }>();

  const mappingStore = useCustomerMappingStore();
  const privacyStore = useCalendarPrivacyStore();

  const cardList = ref<HTMLElement | null>(null);
  const dropHover = ref(false);
  const subLineOverflowing = ref<Record<string, boolean>>({});

  const { hoveredBlockKey, setHoveredBlock, clearHoveredBlock } = useAssignmentHover();
  // Tracks whether the live hover was set here, so unmounting mid-hover — which virtualized
  // scrolling does routinely — clears it without stomping a hover something else has taken.
  const thisCellSetHover = ref(false);

  // The same block key the capacity segments below carry, so pointing at a card rings its
  // capacity everywhere and pointing at any of that capacity rings the card back.
  function onCardHoverStart(group: ScheduleGroup, event: MouseEvent): void {
    measureSubLine(group.key, event);
    setHoveredBlock(groupBlockKey(group));
    thisCellSetHover.value = true;
  }

  function onCardHoverEnd(): void {
    if (!thisCellSetHover.value) return;
    clearHoveredBlock();
    thisCellSetHover.value = false;
  }

  onBeforeUnmount(onCardHoverEnd);

  // A block selected from outside this cell — the PO search, a deep link — may sit below the
  // fold of the scrolling list, where its ring shows nothing. Bring it into view. A card
  // clicked here is already in view, and 'nearest' leaves it where it is. Immediate, because
  // a virtualized column mounts with the selection already made.
  watch(
    () => props.selectedBlockKey,
    async (key) => {
      if (!key || !props.cell?.groups.some((group) => group.key === key)) return;
      await nextTick();
      cardList.value?.querySelector<HTMLElement>(`[data-group-key="${CSS.escape(key)}"]`)?.scrollIntoView({ block: 'nearest' });
    },
    { immediate: true },
  );

  // Whether the type breakdown (or a manual block's note) is clipped can only be known
  // once it is laid out in the real font, so it is measured live — on the card's own
  // mouseenter, which fires before the tooltip activator's and so settles `disabled`
  // before Vuetify decides whether to open.
  function measureSubLine(key: string, event: MouseEvent): void {
    const line = (event.currentTarget as HTMLElement).querySelector('.schedule-card__subline');
    if (!line) return;
    subLineOverflowing.value = { ...subLineOverflowing.value, [key]: line.scrollWidth > line.clientWidth };
  }

  const manualStyle = {
    borderLeftColor: '#FDE68A',
    background: 'repeating-linear-gradient(45deg, #FEF9C3 0 6px, #FEF3C7 6px 12px)',
  };

  // Right-clicking anywhere on a card opens its menu, by pressing the button the menu
  // already belongs to. The menu stays anchored to that button rather than to the pointer,
  // which keeps one activator and one code path instead of two.
  function openCardMenu(event: MouseEvent): void {
    (event.currentTarget as HTMLElement).querySelector<HTMLButtonElement>('.schedule-card__menu-button')?.click();
  }

  function calendarLink(group: ScheduleGroup): RouteLocationRaw {
    return { path: '/capacity-calendar', query: calendarLinkQuery(group, props.weekKey) };
  }

  function activityLink(group: ScheduleGroup): RouteLocationRaw {
    return { path: '/po-activity', query: activityLinkQuery(group) };
  }

  // Reading comes first even for a writer: the dialog opens on the note and offers Edit and
  // Delete from there, so the only thing the menu has to distinguish is having one or not.
  function noteMenuLabel(group: ScheduleGroup): string {
    return group.notes?.trim() ? 'View note' : 'Add note';
  }

  function canViewLineItems(group: ScheduleGroup): boolean {
    return groupHasLineItems(group) && Boolean(props.vendorId);
  }

  function onDragStart(group: ScheduleGroup, event: DragEvent): void {
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
    emit('dragStart', group);
  }

  function onDragOver(event: DragEvent): void {
    if (!props.dropEligible) return;
    event.preventDefault();
    dropHover.value = true;
  }

  function onDragLeave(): void {
    dropHover.value = false;
  }

  function onDrop(event: DragEvent): void {
    if (!props.dropEligible) return;
    event.preventDefault();
    dropHover.value = false;
    emit('drop');
  }

  function groupColor(group: ScheduleGroup, index: number): string {
    if (!group.poNumber) return UNMAPPED_PO_COLOR;
    return resolvePoSegmentColor(group.poNumber, mappingStore.items, {
      hideCustomerMappings: privacyStore.hideCustomerMappings,
      index,
    });
  }

  // Clamped to one line in the block. The same for every block type since the comment moved
  // up to the title: a manual block spans capacity types just as a PO does, so its split is
  // worth as much here.
  function subLine(group: ScheduleGroup): string {
    return group.portions.map((portion) => `${portion.typeCode} ${portion.quantity.toLocaleString()}`).join(' · ');
  }

  const isOverbooked = computed(() => (props.cell?.overbookedTypes.length ?? 0) > 0);

  const meterWidth = computed(() => {
    const cell = props.cell;
    if (!cell) return '0%';
    if (isOverbooked.value) return '100%';
    const planned = cell.scheduledTotal + cell.remainingTotal;
    if (planned <= 0) return '0%';
    return `${Math.min(FULL_PERCENT, Math.round((cell.scheduledTotal / planned) * FULL_PERCENT))}%`;
  });

  const overbookedLabel = computed(() =>
    (props.cell?.overbookedTypes ?? []).map((entry) => `${entry.typeCode} over by ${entry.by.toLocaleString()}`).join(' · '),
  );
</script>

<style scoped>
  .schedule-cell--drop-target {
    outline: 2px dashed rgb(37 99 235);
    outline-offset: -2px;
    background: rgb(239 246 255);
  }

  /* Stacked so the connector overlay (z-index 7 on .schedule-grid__connectors) can sit just
     above it and run over the cards. Still below the pinned column (10). */
  .schedule-card {
    position: relative;
    z-index: 6;
  }

  .schedule-card--selected {
    box-shadow: 0 0 0 2px rgb(37 99 235);
  }

  /* Matches the capacity bar's hover ring, so a block reads the same however you reach it.
     Selection wins when both land on one card — it is the more durable state. */
  .schedule-card--hovered {
    box-shadow: 0 0 0 2px rgb(191 219 254);
  }

  .schedule-card--selected.schedule-card--hovered {
    box-shadow: 0 0 0 2px rgb(37 99 235);
  }

  /* Always present rather than revealed on hover: it holds its own width either way, so
     showing it costs no layout and saves the user guessing that it is there. The glyph is
     small by nature, so the button carries a 24px box around it to click at. */
  .schedule-card__menu-button {
    font-size: 1.125rem;
    line-height: 1;
  }

  .schedule-card-menu {
    display: flex;
    flex-direction: column;
    min-width: 12rem;
    padding: 0.25rem;
    border: 1px solid rgb(226 232 240);
    border-radius: 0.5rem;
    background: white;
    box-shadow: 0 8px 24px rgb(15 23 42 / 12%);
  }

  .schedule-card-menu__item {
    padding: 0.375rem 0.5rem;
    border-radius: 0.25rem;
    color: rgb(51 65 85);
    font-size: 0.75rem;
    text-align: left;
    width: 100%;
  }

  .schedule-card-menu__item:hover {
    background: rgb(241 245 249);
  }

  .schedule-card-menu__item--danger {
    color: rgb(185 28 28);
  }

  .schedule-card-menu__item--danger:hover {
    background: rgb(254 242 242);
  }
</style>
